import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from 'react-router-dom';
import logo from '../../assets/images/mereos-logo-with-text.svg';
import { useTranslation } from 'react-i18next';
import './header.scss';
import caretIcon from '../../assets/images/carot-down.svg';
import LanguageSelector from '../LanguageSelector';

const NAV_BREAKPOINT = 1024;

const Header = () => {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const languageSelectorRef = useRef(null);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [mobileOpenDropdown, setMobileOpenDropdown] = useState(null);
	const [isMobileNav, setIsMobileNav] = useState(
		() => typeof window !== 'undefined' && window.innerWidth <= NAV_BREAKPOINT
	);

	const normalizedPath = pathname.replace(/\/+$/, '') || '/';

	const isExactPath = (path) => {
		const normalizedTarget = path.replace(/\/+$/, '') || '/';
		return normalizedPath === normalizedTarget;
	};

	const isSectionActive = (sectionPath) => {
		if (sectionPath === '/') {
			return normalizedPath === '/' || normalizedPath === '/npm';
		}
		return (
			normalizedPath === sectionPath ||
			normalizedPath.startsWith(`${sectionPath}/`)
		);
	};

	const getActiveMobileDropdownId = () => {
		if (normalizedPath === '/' || normalizedPath === '/npm') return 'get-started';
		if (normalizedPath.startsWith('/compatibility')) return 'compatibility';
		if (normalizedPath === '/profile' || normalizedPath === '/incident-level') return 'profile';
		if (normalizedPath.startsWith('/features')) return 'features';
		if (normalizedPath.startsWith('/documentation')) return 'documentation';
		return null;
	};

	const closeMobileMenu = () => {
		setIsMenuOpen(false);
		setMobileOpenDropdown(null);
		languageSelectorRef.current?.close();
	};

	const closeMobileMenuOnly = () => {
		setIsMenuOpen(false);
		setMobileOpenDropdown(null);
	};

	const toggleMobileMenu = () => {
		setIsMenuOpen((current) => {
			const next = !current;
			if (next) {
				languageSelectorRef.current?.close();
				setMobileOpenDropdown(getActiveMobileDropdownId());
			} else {
				setMobileOpenDropdown(null);
			}
			return next;
		});
	};

	const toggleMobileDropdown = (id) => {
		setMobileOpenDropdown((current) => (current === id ? null : id));
	};

	useEffect(() => {
		closeMobileMenu();
	}, [pathname]);

	useEffect(() => {
		const mediaQuery = window.matchMedia(`(max-width: ${NAV_BREAKPOINT}px)`);
		const handleChange = (event) => setIsMobileNav(event.matches);

		setIsMobileNav(mediaQuery.matches);
		mediaQuery.addEventListener('change', handleChange);

		return () => mediaQuery.removeEventListener('change', handleChange);
	}, []);

	useEffect(() => {
		if (!isMenuOpen) return undefined;

		const handleEscape = (event) => {
			if (event.key === 'Escape') closeMobileMenu();
		};

		document.body.style.overflow = 'hidden';
		document.addEventListener('keydown', handleEscape);

		return () => {
			document.body.style.overflow = '';
			document.removeEventListener('keydown', handleEscape);
		};
	}, [isMenuOpen]);

	const renderNavDropdown = (id, label, sectionPath, links, parentTo = null) => {
		const isMobileOpen = mobileOpenDropdown === id;
		const isActive =
			isSectionActive(sectionPath) ||
			(parentTo ? isSectionActive(parentTo) : false) ||
			links.some(({ to, exact }) => (exact ? isExactPath(to) : isSectionActive(to)));
		const triggerClass = `dropdown-nav-text dropdown-nav-trigger${isActive ? ' dropdown-nav-text--active' : ''}`;
		const useDesktopParentTrigger = parentTo && !isMobileNav;

		return (
			<div className={`header-nav-dropdown${isMobileOpen ? ' header-nav-dropdown--open' : ''}`}>
				{useDesktopParentTrigger ? (
					<div className="dropdown-nav-trigger-group">
						<Link
							to={parentTo}
							className={triggerClass}
							onClick={closeMobileMenu}
						>
							<span>{label}</span>
						</Link>
						<span className="dropdown-nav-caret-btn dropdown-nav-caret-btn--desktop" aria-hidden="true">
							<img src={caretIcon} alt="" className="dropdown-nav-caret" />
						</span>
					</div>
				) : (
					<button
						type="button"
						className={triggerClass}
						onClick={() => isMobileNav && toggleMobileDropdown(id)}
						aria-expanded={isMobileOpen}
						aria-haspopup="true"
					>
						<span>{label}</span>
						<img src={caretIcon} alt="" className="dropdown-nav-caret" />
					</button>
				)}
				<div className="nav-dropdown-menu" role="menu">
					{links.map(({ to, label: linkLabel, exact = false }) => (
						<NavLink
							key={to}
							to={to}
							end={Boolean(exact)}
							className={({ isActive: linkActive }) =>
								`nav-dropdown-menu__link${linkActive ? ' nav-dropdown-menu__link--active' : ''}`
							}
							role="menuitem"
							onClick={closeMobileMenu}
						>
							{linkLabel}
						</NavLink>
					))}
				</div>
			</div>
		);
	};

	return (
		<>
			<header className={`header-container${isMenuOpen ? ' header-container--menu-open' : ''}`}>
				<div className="header-content">
					<div className="header-brand">
						<Link to="/" className="logo-container" onClick={closeMobileMenu}>
							<img alt={t('overview')} src={logo} />
						</Link>
					</div>

					<nav className={`header-nav ${isMenuOpen ? 'open' : ''}`}>
						<div className="header-offcanvas-top">
							<Link to="/" className="header-offcanvas-logo" onClick={closeMobileMenu}>
								<img alt={t('overview')} src={logo} />
							</Link>
							<button
								type="button"
								className="header-offcanvas-close"
								onClick={closeMobileMenu}
								aria-label="Close menu"
							>
								<span className="header-offcanvas-close__icon" aria-hidden="true">
									<span className="header-offcanvas-close__line" />
									<span className="header-offcanvas-close__line" />
								</span>
							</button>
						</div>

						<div className="header-offcanvas-body">
							{renderNavDropdown('get-started', t('get_started'), '/', [
								{ to: '/', label: t('cdn_tab_title'), exact: true },
								{ to: '/npm', label: t('npm_tab_title'), exact: true },
							])}

							{renderNavDropdown('compatibility', t('compatibility'), '/compatibility', [
								{ to: '/compatibility/browser', label: t('browser_compatibility') },
								{ to: '/compatibility/system', label: t('system_compatibility') },
							])}

							{renderNavDropdown('profile', t('profile_creation'), '/profile', [
								{ to: '/profile', label: t('profile_creation'), exact: true },
								{ to: '/incident-level', label: t('incident_level'), exact: true },
							])}

							{renderNavDropdown(
								'features',
								t('feature'),
								'/features',
								[
									{ to: '/features', label: t('features'), exact: true },
									{ to: '/features/recording', label: t('recording_options') },
									{ to: '/features/lockdown', label: t('lockdown_options') },
									{ to: '/features/verification', label: t('verification_options') },
									{ to: '/features/system', label: t('system_options') },
									{ to: '/features/ai', label: t('ai_features') },
									{ to: '/features/quiz-tools', label: t('quiz_tools') },
								],
								'/features'
							)}

							{renderNavDropdown('documentation', t('documentation'), '/documentation', [
								{ to: '/documentation/exceptions', label: t('library_troubleshooting') },
								{ to: '/documentation/api-errors', label: t('api_troubleshooting') },
								{ to: '/documentation/corder-api', label: t('api_documentation') },
							])}
						</div>
					</nav>

					<div className="header-actions">
						<LanguageSelector
							ref={languageSelectorRef}
							hideLabelOnMobile
							onOpen={closeMobileMenuOnly}
						/>

						<button
							className={`hamburger-menu${isMenuOpen ? ' hamburger-menu--open' : ''}`}
							onClick={toggleMobileMenu}
							aria-label="Toggle navigation"
							aria-expanded={isMenuOpen}
						>
							<span className="hamburger-menu__box" aria-hidden="true">
								<span className="hamburger-menu__line" />
								<span className="hamburger-menu__line" />
								<span className="hamburger-menu__line" />
							</span>
						</button>
					</div>
				</div>
			</header>

			<div
				className={`header-offcanvas-backdrop${isMenuOpen ? ' header-offcanvas-backdrop--visible' : ''}`}
				onClick={closeMobileMenu}
				role="presentation"
				aria-hidden={!isMenuOpen}
			/>
		</>
	);
};

export default Header;
